import { basename } from "node:path";
import { createHash } from "node:crypto";
import { readFile } from "node:fs/promises";
import { spawn } from "node:child_process";
import { createInterface } from "node:readline/promises";
import { stdin as input, stdout as output } from "node:process";
import type { Command } from "commander";
import type { ResolvedConfig } from "../config.js";
import { CliApiClient, type CliSubmissionResponse } from "../api/cli-api.js";
import { CLI_VERSION } from "../constants.js";
import { AspError } from "../utils/errors.js";
import {
  printInfo,
  printJson,
  printSuccess,
  printTable,
  printWarn,
} from "../utils/output.js";
import { formatPlatformLabel, requireCredentials } from "../utils/platform.js";
import { appendOptionalArtifactFields, looksLikeSubmissionId } from "../utils/artifact-publish.js";
import { recordSkillPublish } from "../utils/publish-registry.js";
import { resolveSkillSubmissionId } from "../utils/submission-resolve.js";
import { pollUntilTerminal } from "../utils/submission-poll.js";
import {
  readSkillManifestFromZip,
  type SkillManifest,
} from "../utils/skill-manifest.js";

interface SearchOptions {
  category?: string;
  source?: "official" | "verified" | "community";
  tag?: string;
  sort?: "recommended" | "downloads" | "updated_at";
  pageSize?: number;
  cursor?: string;
}

interface InfoOptions {
  version?: string;
}

interface InstallOptions {
  version?: string;
  via?: "qwenpaw";
}

interface PublishOptions {
  url?: string;
  artifactId?: string;
  version?: string;
  category?: SkillCategory;
  tags?: string;
  repoUrl?: string;
  publishMode?: "auto_after_scan";
  noWait?: boolean;
}

interface PublishStatusOptions {
  watch?: boolean;
}

type SkillCategory =
  | "engineering development"
  | "data research"
  | "document office"
  | "design creation"
  | "automation integration"
  | "product management"
  | "marketing growth"
  | "security compliance"
  | "education knowledge"
  | "plugin development"
  | "skills management"
  | "others";

const SKILL_CATEGORY_OPTIONS: Array<{
  value: SkillCategory;
  label: string;
}> = [
  { value: "engineering development", label: "工程开发" },
  { value: "data research", label: "数据研究" },
  { value: "document office", label: "文档办公" },
  { value: "design creation", label: "设计创作" },
  { value: "automation integration", label: "自动化集成" },
  { value: "product management", label: "产品管理" },
  { value: "marketing growth", label: "营销增长" },
  { value: "security compliance", label: "安全合规" },
  { value: "education knowledge", label: "教育知识" },
  { value: "plugin development", label: "Plugin 开发" },
  { value: "skills management", label: "Skills 管理" },
  { value: "others", label: "其它" },
];

function getClient(getConfig: () => ResolvedConfig): CliApiClient {
  return new CliApiClient(getConfig());
}

function parseTags(tags?: string): string[] | undefined {
  if (!tags) return undefined;
  return tags
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 3);
}

async function runSearch(
  getConfig: () => ResolvedConfig,
  query: string | undefined,
  opts: SearchOptions,
): Promise<void> {
  const config = getConfig();
  const client = getClient(getConfig);
  const response = await client.searchSkills({
    q: query,
    category: opts.category,
    source: opts.source,
    tag: opts.tag,
    sort: opts.sort,
    page_size: opts.pageSize,
    cursor: opts.cursor,
  });

  if (config.json) {
    printJson(response);
    return;
  }

  if (response.items.length === 0) {
    printInfo("No skills found.");
    return;
  }

  const rows = response.items.map((item) => [
    item.skill_id,
    String(item.latest_version ?? "-"),
    String(item.category ?? "-"),
    String(item.source ?? "-"),
  ]);
  printTable(["skill_id", "version", "category", "source"], rows);
  if (response.next_cursor) {
    printInfo(`Next cursor: ${response.next_cursor}`);
  }
}

async function runInfo(
  getConfig: () => ResolvedConfig,
  skillId: string,
  opts: InfoOptions,
): Promise<void> {
  const config = getConfig();
  const client = getClient(getConfig);
  const response = await client.getSkillInfo(skillId, opts.version);
  if (config.json) {
    printJson(response);
    return;
  }

  printInfo(`Skill: ${response.skill_id}`);
  if (response.latest_version) printInfo(`Version: ${response.latest_version}`);
  if (response.scan_status) printInfo(`Scan: ${response.scan_status}`);
  if (response.install_command) printInfo(`Install: ${response.install_command}`);
  if (response.public_url) printInfo(`URL: ${response.public_url}`);
}

async function runVersions(getConfig: () => ResolvedConfig, skillId: string): Promise<void> {
  const config = getConfig();
  const client = getClient(getConfig);
  const response = await client.getSkillVersions(skillId);
  if (config.json) {
    printJson(response);
    return;
  }
  if (response.items.length === 0) {
    printInfo("No published versions.");
    return;
  }
  const rows = response.items.map((item) => [
    item.version,
    item.published_at ?? "-",
    item.deprecated ? "yes" : "no",
  ]);
  printTable(["version", "published_at", "deprecated"], rows);
}

async function runInstall(
  getConfig: () => ResolvedConfig,
  skillId: string,
  opts: InstallOptions,
): Promise<void> {
  const base = getConfig();
  const { config } = await requireCredentials(base, base.platformUrl);
  const client = new CliApiClient(config);
  const intent = await client.createSkillInstallIntent(skillId, opts.version);

  if (base.json) {
    printJson(intent);
    return;
  }

  if (opts.via !== undefined && opts.via !== "qwenpaw") {
    throw new AspError("Only --via qwenpaw is supported now.", {
      code: "INVALID_REQUEST",
    });
  }

  const installCmd = `qwenpaw skill install ${intent.download_url}`;
  printInfo(`Run to install:\n  ${installCmd}`);
  if (intent.sha512) printInfo(`sha512: ${intent.sha512}`);
  if (intent.expires_at) printInfo(`URL expires at: ${intent.expires_at}`);

  const child = spawn("qwenpaw", ["skill", "install", intent.download_url], {
    stdio: "inherit",
  });
  await new Promise<void>((resolve, reject) => {
    child.on("exit", (code) => {
      if (code === 0) resolve();
      else reject(new AspError(`qwenpaw exited with code ${code ?? 1}`, { code: "QWENPAW_FAILED" }));
    });
    child.on("error", reject);
  });
}

async function uploadZipToSignedUrl(upload: {
  url: string;
  method?: string;
  headers?: Record<string, string>;
}, fileBuffer: Buffer): Promise<void> {
  const response = await fetch(upload.url, {
    method: upload.method ?? "PUT",
    headers: upload.headers ?? {},
    body: fileBuffer,
  });
  if (!response.ok) {
    throw new AspError(`Upload failed with HTTP ${response.status}`, {
      code: "UPLOAD_FAILED",
      statusCode: response.status,
    });
  }
}

function buildZipBody(
  filePath: string,
  fileBuffer: Buffer,
  sha512: string,
  opts: PublishOptions & { category: SkillCategory },
): Record<string, unknown> {
  const body = appendOptionalArtifactFields(
    {
      artifact_type: "skill",
      package_source: "zip",
      category: opts.category,
      tags: parseTags(opts.tags),
      filename: basename(filePath),
      size_bytes: fileBuffer.byteLength,
      sha512,
      publish_mode: opts.publishMode ?? "auto_after_scan",
      client: {
        name: "agentscope-platform-cli",
        version: CLI_VERSION,
      },
    },
    {
      artifactId: opts.artifactId,
      version: opts.version,
      repoUrl: opts.repoUrl,
    },
  );
  return body;
}

function buildUrlBody(opts: PublishOptions & { category: SkillCategory }): Record<string, unknown> {
  return appendOptionalArtifactFields(
    {
      artifact_type: "skill",
      package_source: "url",
      source_url: opts.url,
      category: opts.category,
      tags: parseTags(opts.tags),
      publish_mode: opts.publishMode ?? "auto_after_scan",
      client: {
        name: "agentscope-platform-cli",
        version: CLI_VERSION,
      },
    },
    {
      artifactId: opts.artifactId,
      version: opts.version,
      repoUrl: opts.repoUrl,
    },
  );
}

function parseCategory(inputValue: string | undefined): SkillCategory | undefined {
  if (!inputValue) return undefined;
  const normalized = inputValue.trim().toLowerCase();
  if (SKILL_CATEGORY_OPTIONS.some((item) => item.value === normalized)) {
    return normalized as SkillCategory;
  }
  return undefined;
}

async function resolvePublishCategory(
  opts: PublishOptions,
  jsonOutput: boolean,
): Promise<SkillCategory> {
  const fromArg = parseCategory(opts.category);
  if (fromArg) return fromArg;

  if (opts.category && !fromArg) {
    throw new AspError(`Invalid --category: ${opts.category}`, {
      code: "INVALID_REQUEST",
      hint: `Use one of: ${SKILL_CATEGORY_OPTIONS.map((item) => item.value).join(", ")}`,
    });
  }

  if (jsonOutput || !input.isTTY || !output.isTTY) {
    throw new AspError("Skill publish requires --category.", {
      code: "INVALID_REQUEST",
      hint: `Use --category with one of: ${SKILL_CATEGORY_OPTIONS.map((item) => item.value).join(", ")}`,
    });
  }

  printInfo("Select skill category:");
  for (const [index, item] of SKILL_CATEGORY_OPTIONS.entries()) {
    printInfo(`${index + 1}) ${item.value} - ${item.label}`);
  }

  const rl = createInterface({ input, output });
  try {
    while (true) {
      const answer = (await rl.question("Enter category number or value: ")).trim();
      const index = Number.parseInt(answer, 10);
      if (!Number.isNaN(index) && index >= 1 && index <= SKILL_CATEGORY_OPTIONS.length) {
        const selected = SKILL_CATEGORY_OPTIONS[index - 1].value;
        printInfo(`Selected category: ${selected}`);
        return selected;
      }
      const byValue = parseCategory(answer);
      if (byValue) {
        printInfo(`Selected category: ${byValue}`);
        return byValue;
      }
      printWarn(`Invalid choice. Use 1-${SKILL_CATEGORY_OPTIONS.length} or category value.`);
    }
  } finally {
    rl.close();
  }
}

async function resolvePublishVersion(jsonOutput: boolean): Promise<string> {
  if (jsonOutput || !input.isTTY || !output.isTTY) {
    throw new AspError("Skill publish requires version.", {
      code: "INVALID_REQUEST",
      hint: "Add `version: 1.0.0` to SKILL.md frontmatter, or pass --version 1.0.0.",
    });
  }

  const rl = createInterface({ input, output });
  try {
    while (true) {
      const answer = (await rl.question("Enter skill version (e.g. 1.0.0): ")).trim();
      if (answer) {
        printInfo(`Using version: ${answer}`);
        return answer;
      }
      printWarn("Version is required. Example: 1.0.0");
    }
  } finally {
    rl.close();
  }
}

function isMissingVersionError(err: unknown): boolean {
  if (!(err instanceof AspError)) return false;
  if (err.code !== "INVALID_REQUEST") return false;
  const message = `${err.message} ${err.hint ?? ""}`.toLowerCase();
  return message.includes("version") || message.includes("skill.md");
}

function printSkillManifestPreview(manifest: SkillManifest | undefined, jsonOutput: boolean): void {
  if (jsonOutput || !manifest) return;
  if (manifest.name) {
    printInfo(`Package skill id: ${manifest.name}`);
  }
  if (manifest.version) {
    printInfo(`Package version: ${manifest.version}`);
  }
}

function validateExplicitSkillOverrides(
  opts: PublishOptions,
  manifest: SkillManifest | undefined,
): void {
  if (opts.artifactId && manifest?.name && opts.artifactId !== manifest.name) {
    throw new AspError(
      `artifact_id mismatch: --artifact-id=${opts.artifactId}, SKILL.md name=${manifest.name}`,
      {
        code: "ARTIFACT_ID_MISMATCH",
        hint: "Remove --artifact-id or fix the value to match SKILL.md.",
      },
    );
  }
  if (opts.version && manifest?.version && opts.version !== manifest.version) {
    throw new AspError(
      `version mismatch: --version=${opts.version}, SKILL.md version=${manifest.version}`,
      {
        code: "VERSION_MISMATCH",
        hint: "Remove --version or fix the value to match SKILL.md.",
      },
    );
  }
}

function resolveSkillPublishFields(
  opts: PublishOptions & { category: SkillCategory },
  manifest: SkillManifest | undefined,
): PublishOptions & { category: SkillCategory } {
  return {
    ...opts,
    artifactId: opts.artifactId ?? manifest?.name,
    version: opts.version ?? manifest?.version,
  };
}

function isMissingCategoryError(err: unknown): boolean {
  if (!(err instanceof AspError)) return false;
  if (err.code !== "INVALID_REQUEST") return false;
  const message = `${err.message} ${err.hint ?? ""}`.toLowerCase();
  return message.includes("category") || message.includes("技术类型") || message.includes("大类");
}

function tryParseSkillIdFromUrl(urlValue: string | undefined): string | undefined {
  if (!urlValue) return undefined;
  try {
    const parsed = new URL(urlValue);
    const segments = parsed.pathname
      .split("/")
      .map((item) => item.trim())
      .filter(Boolean);
    const skillsIdx = segments.findIndex((item) => item.toLowerCase() === "skills");
    if (skillsIdx >= 0 && segments[skillsIdx + 1]) {
      return segments[skillsIdx + 1];
    }
    const last = segments[segments.length - 1];
    if (last?.toLowerCase().endsWith(".zip")) {
      return last.replace(/-\d+\.\d+\.\d+.*\.zip$/i, "").replace(/\.zip$/i, "");
    }
    return undefined;
  } catch {
    return undefined;
  }
}

async function showPublishedSkillStatus(
  client: CliApiClient,
  skillId: string,
  jsonOutput: boolean,
): Promise<void> {
  try {
    const info = await client.getSkillInfo(skillId);
    if (jsonOutput) {
      printJson({
        skill_id: info.skill_id,
        status: "published",
        terminal: true,
        latest_version: info.latest_version,
        scan_status: info.scan_status,
        public_url: info.public_url,
        install_command: info.install_command,
      });
      return;
    }

    printInfo(`Skill: ${info.skill_id}`);
    printInfo("Status: published");
    printInfo("Terminal: yes");
    if (info.latest_version) printInfo(`Version: ${info.latest_version}`);
    if (info.scan_status) printInfo(`Scan: ${info.scan_status}`);
    if (info.public_url) printInfo(`URL: ${info.public_url}`);
    if (info.install_command) printInfo(`Install: ${info.install_command}`);
  } catch (err) {
    if (err instanceof AspError && err.statusCode === 404) {
      throw new AspError(`No submission or published skill found: ${skillId}`, {
        code: "NOT_FOUND",
        hint:
          "If the skill is still scanning, use submission_id from publish output. " +
          "Republish on this machine to cache skill_id mapping locally.",
      });
    }
    throw err;
  }
}

async function runPublish(
  getConfig: () => ResolvedConfig,
  zipPath: string | undefined,
  opts: PublishOptions,
): Promise<void> {
  const base = getConfig();
  const { config } = await requireCredentials(base, base.platformUrl);
  const client = new CliApiClient(config);
  let publishOpts: PublishOptions & { category: SkillCategory };
  try {
    const category = await resolvePublishCategory(opts, base.json);
    publishOpts = { ...opts, category };
  } catch (err) {
    if (!isMissingCategoryError(err)) throw err;
    const category = await resolvePublishCategory({ ...opts, category: undefined }, base.json);
    publishOpts = { ...opts, category };
  }

  const useUrlMode = Boolean(opts.url);
  if (useUrlMode && zipPath) {
    throw new AspError("Do not pass zip path when using --url.", {
      code: "INVALID_REQUEST",
      hint: "Use either `asp skill publish ./skill.zip` or `asp skill publish --url <url>`.",
    });
  }
  if (!useUrlMode && !zipPath) {
    throw new AspError("Zip path is required unless --url is provided.", {
      code: "INVALID_REQUEST",
      hint: "Use `asp skill publish ./skill.zip`.",
    });
  }

  const manifest = useUrlMode ? undefined : await readSkillManifestFromZip(zipPath);
  printSkillManifestPreview(manifest, base.json);
  validateExplicitSkillOverrides(publishOpts, manifest);
  publishOpts = resolveSkillPublishFields(publishOpts, manifest);
  if (!publishOpts.version) {
    publishOpts.version = await resolvePublishVersion(base.json);
  }

  let submission: CliSubmissionResponse;
  const tryCreateSubmission = async (): Promise<CliSubmissionResponse> => {
    if (useUrlMode) {
      return client.createArtifactSubmission(buildUrlBody(publishOpts));
    }
    const fileBuffer = await readFile(zipPath as string);
    const sha512 = createHash("sha512").update(fileBuffer).digest("hex");
    let localSubmission = await client.createArtifactSubmission(
      buildZipBody(zipPath as string, fileBuffer, sha512, publishOpts),
    );
    if (!localSubmission.upload?.url) {
      throw new AspError("Missing upload URL for zip submission.", {
        code: "UPLOAD_URL_MISSING",
      });
    }
    printInfo("Uploading skill zip to object storage...");
    await uploadZipToSignedUrl(localSubmission.upload, fileBuffer);
    localSubmission = await client.completeArtifactSubmission(localSubmission.submission_id, {
      sha512,
      size_bytes: fileBuffer.byteLength,
    });
    return localSubmission;
  };

  try {
    submission = await tryCreateSubmission();
  } catch (err) {
    if (isMissingVersionError(err)) {
      if (base.json || !input.isTTY || !output.isTTY) {
        throw new AspError("SKILL.md in zip is missing version.", {
          code: "INVALID_REQUEST",
          hint:
            "Add `version: 1.0.0` to SKILL.md frontmatter (between --- markers), " +
            "or pass --version 1.0.0.",
        });
      }
      printWarn("Publish failed due to missing version, please enter one and retrying...");
      publishOpts.version = await resolvePublishVersion(base.json);
      submission = await tryCreateSubmission();
    } else if (!isMissingCategoryError(err) || base.json || !input.isTTY || !output.isTTY) {
      throw err;
    } else {
      printWarn("Publish failed due to missing category, please choose one and retrying...");
      const category = await resolvePublishCategory({ ...opts, category: undefined }, base.json);
      publishOpts = { ...publishOpts, category };
      submission = await tryCreateSubmission();
    }
  }

  const urlSkillId = useUrlMode ? tryParseSkillIdFromUrl(publishOpts.url) : undefined;
  await recordSkillPublish({
    submissionId: submission.submission_id,
    platformUrl: config.platformUrl,
    skillId: publishOpts.artifactId ?? urlSkillId,
    sourceUrl: useUrlMode ? publishOpts.url : undefined,
  });

  if (base.json) {
    printJson(submission);
    return;
  }

  printSuccess(`Submission created: ${submission.submission_id}`);
  printInfo(`Initial status: ${submission.status}`);
  const trackRef =
    publishOpts.artifactId ?? urlSkillId ?? submission.submission_id;

  if (opts.noWait) {
    printInfo(`Track progress: asp skill publish status ${trackRef}`);
    printInfo(`Show logs: asp skill publish logs ${trackRef}`);
    return;
  }

  if (!submission.terminal) {
    printSuccess("Upload submitted successfully. Scanning continues asynchronously.");
    printInfo(`Track progress: asp skill publish status ${trackRef} --watch`);
    printInfo(`Show logs: asp skill publish logs ${trackRef}`);
    return;
  }

  if (submission.status === "published") {
    printSuccess(`Published successfully (${formatPlatformLabel(config.platformUrl)})`);
    if (submission.publish?.public_url) {
      printInfo(`URL: ${submission.publish.public_url}`);
    }
    return;
  }

  printWarn(`Submission ended with status: ${submission.status}`);
  if (submission.failure?.message) {
    printWarn(`Failure: ${submission.failure.message}`);
  }
  printInfo(`Inspect logs: asp skill publish logs ${trackRef}`);
}

async function runPublishStatus(
  getConfig: () => ResolvedConfig,
  skillRef: string,
  opts: PublishStatusOptions,
): Promise<void> {
  const base = getConfig();
  const { config } = await requireCredentials(base, base.platformUrl);
  const client = new CliApiClient(config);

  if (looksLikeSubmissionId(skillRef)) {
    const state = opts.watch
      ? await pollUntilTerminal(client, skillRef, true)
      : await client.getArtifactSubmission(skillRef);
    if (base.json) {
      printJson(state);
      return;
    }
    printInfo(`Submission: ${state.submission_id}`);
    printInfo(`Status: ${state.status}`);
    printInfo(`Terminal: ${state.terminal ? "yes" : "no"}`);
    if (state.scan?.status) printInfo(`Scan: ${state.scan.status}`);
    if (state.publish?.public_url) printInfo(`URL: ${state.publish.public_url}`);
    if (state.failure?.message) printWarn(`Failure: ${state.failure.message}`);
    return;
  }

  const submissionId = await resolveSkillSubmissionId(client, skillRef, config.platformUrl);
  if (!submissionId) {
    await showPublishedSkillStatus(client, skillRef, base.json);
    return;
  }

  if (!base.json) {
    printInfo(`Resolved skill id "${skillRef}" -> submission ${submissionId}`);
  }

  const current = await client.getArtifactSubmission(submissionId);
  if (current.terminal && current.status === "published") {
    await showPublishedSkillStatus(client, skillRef, base.json);
    return;
  }

  const state = opts.watch
    ? await pollUntilTerminal(client, submissionId, true)
    : current;

  if (base.json) {
    printJson(state);
    return;
  }

  printInfo(`Submission: ${state.submission_id}`);
  printInfo(`Status: ${state.status}`);
  printInfo(`Terminal: ${state.terminal ? "yes" : "no"}`);
  if (state.scan?.status) printInfo(`Scan: ${state.scan.status}`);
  if (state.publish?.public_url) printInfo(`URL: ${state.publish.public_url}`);
  if (state.failure?.message) printWarn(`Failure: ${state.failure.message}`);
}

async function runPublishLogs(
  getConfig: () => ResolvedConfig,
  skillRef: string,
): Promise<void> {
  const base = getConfig();
  const { config } = await requireCredentials(base, base.platformUrl);
  const client = new CliApiClient(config);
  const submissionId = await resolveSkillSubmissionId(client, skillRef, config.platformUrl);
  if (!submissionId) {
    throw new AspError(`No publish logs found for skill id: ${skillRef}`, {
      code: "NOT_FOUND",
      hint:
        "Use submission_id from publish output, or republish on this machine to cache the mapping.",
    });
  }
  if (submissionId !== skillRef && !base.json) {
    printInfo(`Resolved skill id "${skillRef}" -> submission ${submissionId}`);
  }
  const logs = await client.getArtifactSubmissionLogs(submissionId);

  if (base.json) {
    printJson(logs);
    return;
  }

  if (logs.items.length === 0) {
    printInfo("No logs yet.");
    return;
  }
  const rows = logs.items.map((item) => [item.timestamp, item.level, item.message]);
  printTable(["timestamp", "level", "message"], rows);
}

export function registerSkillCommands(program: Command, getConfig: () => ResolvedConfig): void {
  const skill = program.command("skill").description("Skill marketplace and publish commands");

  skill
    .command("search [query]")
    .description("Search skills")
    .option("--category <code>", "Skill category code")
    .option("--source <source>", "official | verified | community")
    .option("--tag <tag>", "Tag filter")
    .option("--sort <sort>", "recommended | downloads | updated_at")
    .option("--page-size <n>", "Page size", (v) => Number.parseInt(v, 10))
    .option("--cursor <cursor>", "Cursor for next page")
    .action(async (query: string | undefined, opts: SearchOptions) => {
      await runSearch(getConfig, query, opts);
    });

  skill
    .command("info <skillId>")
    .description("Show skill details")
    .option("--version <version>", "Specify version")
    .action(async (skillId: string, opts: InfoOptions) => {
      await runInfo(getConfig, skillId, opts);
    });

  skill
    .command("versions <skillId>")
    .description("List published versions")
    .action(async (skillId: string) => {
      await runVersions(getConfig, skillId);
    });

  skill
    .command("install <skillId>")
    .description("Install skill via qwenpaw")
    .option("--version <version>", "Specify version")
    .option("--via <tool>", "Install tool (qwenpaw)", "qwenpaw")
    .action(async (skillId: string, opts: InstallOptions) => {
      await runInstall(getConfig, skillId, opts);
    });

  const publish = skill
    .command("publish [zipPath]")
    .description("Publish skill from local zip or URL")
    .option("--url <url>", "Publish from remote zip URL")
    .option("--artifact-id <id>", "Optional skill id override (must match SKILL.md in zip mode)")
    .option("--version <version>", "Skill version (prompted if SKILL.md has no version)")
    .option(
      "--category <code>",
      "Skill category code (engineering development|data research|document office|design creation|automation integration|product management|marketing growth|security compliance|education knowledge|plugin development|skills management|others)",
    )
    .option("--tags <csv>", "Optional tags, comma-separated, max 3")
    .option("--repo-url <url>", "Repository URL")
    .option("--publish-mode <mode>", "Publish mode", "auto_after_scan")
    .option("--no-wait", "Do not wait for terminal status")
    .action(async (zipPath: string | undefined, opts: PublishOptions) => {
      await runPublish(getConfig, zipPath, opts);
    });

  publish
    .command("status <skillId>")
    .description("Show publish status by skill id or submission id")
    .option("--watch", "Poll until terminal status", false)
    .action(async (skillId: string, opts: PublishStatusOptions) => {
      await runPublishStatus(getConfig, skillId, opts);
    });

  publish
    .command("logs <skillId>")
    .description("Show publish logs by skill id or submission id")
    .action(async (skillId: string) => {
      await runPublishLogs(getConfig, skillId);
    });
}
